import { Dispatch, SetStateAction, useEffect, useMemo } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Modal from "../../layouts/Modal";
import { LogReturnRequest, ReturnChannel } from "../../models/returns/IReturnsApi";
import { reasonGroupLabel, reasonsByGroup } from "../../utils/returnsRules";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface LogReturnForm {
  channel: ReturnChannel;
  reference: string;
  reason: string;
  buyerComment: string;
  buyerUsername: string;
}

interface LogReturnModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSubmit: (body: LogReturnRequest) => void;
  submitting: boolean;
  error?: string | null;
}

// A Tag no. has letters in it (GP-48213); a PartPro order number is digits, "PP-20931" or similar.
const looksLikeTag = (reference: string) => /[A-Za-z]/.test(reference) && !/^pp-?\d+$/i.test(reference);

/**
 * Logs a PartPro or counter return by hand. eBay returns arrive on their own from the sync, so
 * they're not offered here. The reason list is eBay's, grouped the way the spec groups it, so
 * every return is reported the same way whatever the channel.
 */
export default function LogReturnModal({ open, setOpen, onSubmit, submitting, error }: LogReturnModalProps) {
  const { t } = useTranslation("returns");
  const { register, handleSubmit, setValue, watch, reset } = useForm<LogReturnForm>({
    defaultValues: { channel: "partpro", reference: "", reason: "", buyerComment: "", buyerUsername: "" },
  });

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const channelOptions = useMemo(
    () => [
      { id: "partpro", name: "PartPro" },
      { id: "counter", name: t("Counter") },
    ],
    [t]
  );

  const reasonOptions = useMemo(
    () => reasonsByGroup().flatMap(({ group, reasons }) => reasons.map((r) => ({ id: r.code, name: `${t(r.label)} · ${reasonGroupLabel(group)}` }))),
    [t]
  );

  const submit = (form: LogReturnForm) => {
    const reference = form.reference.trim();
    onSubmit({
      channel: form.channel,
      tagNumber: looksLikeTag(reference) ? reference : undefined,
      orderNumber: looksLikeTag(reference) ? undefined : reference || undefined,
      reason: form.reason,
      buyerComment: form.buyerComment.trim(),
      buyerUsername: form.buyerUsername.trim() || undefined,
      via: "desktop",
    });
  };

  const reason = watch("reason");
  const reference = watch("reference");

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-lg">
      <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900">{t("Log a return")}</h2>
          <p className="mt-0.5 text-[12px] text-gray-500">{t("For PartPro and counter returns. eBay returns come in on their own.")}</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectInput label={t("Channel")} register={register} registerName="channel" setValue={setValue} watch={watch} options={channelOptions} rounded />
          <TextInput label={t("Order or Tag no.")} register={register} registerName="reference" placeholder="PP-20931 / GP-45877" rounded required />
        </div>
        <SelectInput label={t("Reason (eBay's list)")} register={register} registerName="reason" setValue={setValue} watch={watch} options={reasonOptions} rounded placeholder={t("Choose a reason")} cols="" />
        <TextInput label={t("Buyer's words")} register={register} registerName="buyerComment" richText rows={3} rounded cols="" placeholder={t("What the buyer said, in their words")} />
        <TextInput label={t("Buyer")} register={register} registerName="buyerUsername" rounded cols="" placeholder={t("Name or username (optional)")} />
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={() => setOpen(false)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            {t("Cancel")}
          </button>
          <ButtonInput label={t("Log return")} isSubmit isLoading={submitting} disabled={!reason || !reference?.trim()} onClick={undefined} classes={undefined} />
        </div>
      </form>
    </Modal>
  );
}
